import { useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import * as yup from 'yup';
import cn from 'classnames';

import type { FormEvent } from 'react';
import type { SchemaOf } from 'yup';

import logEvent, { setUserPropertyAmplitude } from 'analytics/logEvent';

import useTranslationAndTypograph from 'hooks/useTranslationAndTypograph';
import useValidation from 'hooks/useValidation';

import pagesUrls from 'constants/pagesUrls';

import ALink from 'components/basic/ALink';
import Button from 'components/basic/Button';
import Checkbox from 'components/basic/Checkbox';
import ErrorLabel from 'components/basic/ErrorLabel';
import InputText from 'components/basic/InputText';
import InputPassword from 'components/basic/InputPassword';
import { BodyM, Caption } from 'components/basic/Typography';
import SocialLogins from 'components/complex/SocialLogins';

import type { AppState } from 'redux-store';
import { useAppDispatch, useAppSelector } from 'redux-store';
import type { RegistrationRequestType } from 'redux-store/slices/profile/types';
import registrationThunk from 'redux-store/thunks/profile/registrationThunk';

import s from './RegistrationForm.module.scss';

export default function RegistrationForm({
  redirectUrl = '',
}: {
  redirectUrl?: string;
}): JSX.Element {
  const registrationError = useAppSelector<AppState['profile']['error']>(
    appState => appState.profile.error,
  );
  const providers = useAppSelector(state => state.profile.providers);
  const { region } = useAppSelector(state => state.ui);

  const t = useTranslationAndTypograph();

  const schema = useMemo((): SchemaOf<RegistrationRequestType> => {
    return yup.object().shape({
      email: yup.string().email('Invalid email format').min(1).required('Please enter your email'),
      password: yup.string().min(6).required('Please enter your password'),
    });
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // const isEmailOptInUncheckedDefault = region !== 'US';
  const isEmailOptInUncheckedDefault = true;
  const [isEmailOptInChecked, emailOptInChecked] = useState(isEmailOptInUncheckedDefault);

  const { validationErrors, validateAllFields, doValidate } =
    useValidation<RegistrationRequestType>(
      { email, password },
      schema,
    );
  const dispatch = useAppDispatch();

  useEffect(() => {
    Cookies.set('next-auth-email-opt-in', JSON.stringify(isEmailOptInChecked));
  }, [isEmailOptInChecked]);

  const buttonClickLogEvent = (method: string): void => {

    logEvent('Web_Auth_Sign-up_Button_Clicked', {
      method: method,
      'e-mail_opt-in': isEmailOptInChecked,
    });

    const marketingCommunicationConsent = isEmailOptInChecked === true ? 'granted' : 'not_provided';
    setUserPropertyAmplitude('marketing_communication_consent', marketingCommunicationConsent);
  }

  async function doRegistration(e: FormEvent): Promise<void> {
    e.preventDefault();

    buttonClickLogEvent('email');

    const isValidationPassed = await validateAllFields();

    if (isValidationPassed) {
      setIsLoading(true);

      Cookies.set('next-auth-redirect-url', redirectUrl);

      try {
        await dispatch(
          registrationThunk({
            email,
            password,
          }),
        ).unwrap();
      }
      catch(error: any) {
        setIsLoading(false);

        if (error?.message === 'User with this email already exists') {
          logEvent('Web_Auth_Registration_Exist_User_Error');
        }
        else {
          logEvent('Web_Auth_Registration_Error', {
            error: error?.message ?? 'Unknown error'
          });
        }
      }
      

      // setIsLoading(false);
    }
  }

  return (
    <>
      <form onSubmit={doRegistration}>
        <InputText<RegistrationRequestType>
          labelText={t('Email')}
          value={email}
          name="email"
          onChange={setEmail}
          doValidate={doValidate}
          errorMessage={validationErrors.email}
          autocomplete="off"
          className="mb-2 cy-registration-email-input"
        />
        <InputText<RegistrationRequestType>
          labelText={t('Password')}
          value={password}
          name="password"
          type="password"
          onChange={setPassword}
          doValidate={doValidate}
          errorMessage={validationErrors.password}
          autocomplete="off"
          className="mb-2 cy-registration-password-input"
        />
        {registrationError && (
          <ErrorLabel className="mb-8">{t(registrationError)}</ErrorLabel>
        )}
        <Checkbox
          id="2"
          name="2"
          className={cn(s.checkbox, 'cy-email-opt-in')}
          labelText={
            <Caption className={s.checkboxText}>
              I explicitly agree to receive communications from Welltory Inc. about new features, research studies, and special offers developed with selected partners to support my health and wellness. I understand that I can withdraw this consent or unsubscribe at any time
            </Caption>
          }
          checked={isEmailOptInChecked}
          onChange={emailOptInChecked}
        />
        <Button
          type="submit"
          disabled={isLoading}
          isLoading={isLoading}
          className={cn(s.button, 'mb-3 cy-registration-button')}
          size="medium"
        >
          {t('Sign up')}
        </Button>
      </form>
      <Caption className="mb-3">{t('or continue with')}</Caption>
      <SocialLogins
        providers={providers}
        redirectUrl={redirectUrl}
        showProviderName={false}
        buttonClickLogEvent={buttonClickLogEvent}
      />
      <Caption className={cn('mt-6 cy-boring-links-form', s.boringLinks)}>
        {t('By signing up or continuing with Apple, Facebook, or Google, you confirm that you have read our')}{' '}
        <ALink
          href={pagesUrls.privacy}
          className="cy-privacy-form-link"
          onClick={(): void => {
            logEvent('Web_Auth_Sign-up_Privacy_Link_Clicked');
          }}
          target="_blank"
        >
          {t('Privacy Policy')}{' '}
        </ALink>
        {t('and agree to our')}{' '}
        <ALink
          href={pagesUrls.terms}
          onClick={(): void => {
            logEvent('Web_Auth_Sign-up_Terms_Link_Clicked');
          }}
          className="cy-term-of-use-form-link"
          target="_blank"
        >
          {t('Terms of Service')}.
        </ALink>
      </Caption>
    </>
  );
}
